import { FileImage } from "@phosphor-icons/react";
import { getFileInfo, getFileTypeDescription } from "../../utils/file-types";
import { ValueFromFile } from "../api-playground/file-input";
import { FilePlaceholder } from "./file-placeholder";
import { ModelEditor } from "./model-editor";

interface FileRendererProps {
  filename: string;
  content: string | Blob;
  isDark?: boolean;
  className?: string;
}

export function FileRenderer({
  filename,
  content,
  isDark = false,
  className = "",
}: FileRendererProps) {
  const fileInfo = getFileInfo(filename);

  if (!fileInfo.isPreviewable) {
    return <FilePlaceholder filename={filename} className={className} />;
  }

  // Route safe images to BlobImagePreview component
  if (fileInfo.type === "safe_image") {
    if (content instanceof Blob) {
      // Create a File from the Blob and use BlobImagePreview
      const file = new File([content], filename, { type: content.type });
      return (
        <BlobImagePreview
          filename={filename}
          file={file}
          className={className}
        />
      );
    }

    return <FilePlaceholder filename={filename} className={className} />;
  }

  // Route text files to Monaco Editor (only strings)
  if (typeof content === "string") {
    return (
      <ModelEditor
        filename={filename}
        content={content}
        isDark={isDark}
        className={className}
      />
    );
  }

  // Fallback for non-previewable content
  return <FilePlaceholder filename={filename} className={className} />;
}

// Component for showing Blob images with confirmation UI
function BlobImagePreview({
  filename,
  file,
  className = "",
}: {
  filename: string;
  file: File;
  className?: string;
}) {
  const typeDescription = getFileTypeDescription(filename);

  return (
    <div className={`max-h-[550px] flex flex-col ${className} overflow-hidden`}>
      <div className="flex items-center justify-between p-4 border-b border-r8-gray-6 bg-r8-gray-1 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <FileImage size={20} className="text-r8-blue-9 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-r8-sm font-medium text-r8-gray-12 truncate">
              {filename}
            </div>
            <div className="text-r8-xs text-r8-gray-11 truncate">
              {typeDescription}
              {file.size > 0 && ` • ~${Math.round(file.size / 1024)}KB`}
            </div>
          </div>
        </div>
      </div>

      {/* Image display area */}
      <div className="flex-1 flex items-center justify-center bg-r8-gray-1 overflow-hidden min-h-0">
        <div className="flex flex-col p-8 overflow-hidden [&_img]:h-full h-full">
          <ValueFromFile file={file} name={filename} />
        </div>
      </div>
    </div>
  );
}
